import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, claveMes, nombreMes } from '../lib/format';
import { q } from '../lib/vinculos';
import {
  ICONOS_PRESUPUESTO, gastadoPorPresupuesto, gastosDelPresupuesto,
  estadoPresupuesto, totalesPresupuestos,
} from '../lib/presupuestos';
import { useAccion } from '../lib/useAccion';
import ErrorBanner from './ErrorBanner';

const formVacio = { nombre: '', monto_mensual: '', icono: '🧺' };

function Barra({ pct, color }) {
  return (
    <div style={{ background: 'var(--border)', borderRadius: 99, height: 10, overflow: 'hidden', margin: '8px 0 4px' }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.4s ease' }} />
    </div>
  );
}

function Presupuestos({ userId }) {
  const [presupuestos, setPresupuestos] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState(claveMes(hoyISO()));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(formVacio);
  const [editandoId, setEditandoId] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [verArchivados, setVerArchivados] = useState(false);
  const { saving, error, setError, run } = useAccion();

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [p, m] = await Promise.all([
      q(supabase.from('presupuestos').select('*').eq('user_id', userId).order('created_at')),
      q(supabase.from('bolsillo_movimientos').select('id, fecha, tipo, monto, descripcion, presupuesto_id').eq('user_id', userId).eq('tipo', 'egreso')),
    ]);
    setPresupuestos(p || []);
    setMovimientos(m || []);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    const datos = {
      nombre: form.nombre.trim(),
      monto_mensual: parseFloat(form.monto_mensual) || 0,
      icono: form.icono,
    };
    const ok = await run(() => (editandoId
      ? q(supabase.from('presupuestos').update(datos).eq('id', editandoId))
      : q(supabase.from('presupuestos').insert({ user_id: userId, ...datos }))), loadData);
    if (ok) {
      setForm(formVacio);
      setShowForm(false);
      setEditandoId(null);
    }
  };

  const handleEditar = (p) => {
    setForm({ nombre: p.nombre, monto_mensual: p.monto_mensual, icono: p.icono || '🧺' });
    setEditandoId(p.id);
    setShowForm(true);
  };

  const handleArchivar = (p) =>
    run(() => q(supabase.from('presupuestos').update({ activo: !p.activo }).eq('id', p.id)), loadData);

  const handleEliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el presupuesto "${p.nombre}"?\n\nLos gastos que tenía asignados NO se borran: solo dejan de estar clasificados acá.`)) return;
    await run(() => q(supabase.from('presupuestos').delete().eq('id', p.id)), loadData);
  };

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  const gastado = gastadoPorPresupuesto(movimientos, mes);
  const visibles = presupuestos.filter(p => (verArchivados ? !p.activo : p.activo));
  const totales = totalesPresupuestos(presupuestos.filter(p => p.activo), gastado);
  const sinClasificar = movimientos
    .filter(m => !m.presupuesto_id && claveMes(m.fecha) === mes)
    .reduce((s, m) => s + Number(m.monto || 0), 0);

  const mesesDisponibles = [...new Set([claveMes(hoyISO()), ...movimientos.map(m => claveMes(m.fecha))])]
    .filter(Boolean).sort((a, b) => b.localeCompare(a)).slice(0, 12);

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Presupuestos</h2>
          <p className="page-subtitle">Cuánto quieres gastar al mes en cada cosa, y cuánto llevas</p>
        </div>
        <button className="btn-primary" onClick={() => { setForm(formVacio); setEditandoId(null); setShowForm(s => !s); }}>
          {showForm ? 'Cancelar' : '+ Nuevo presupuesto'}
        </button>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Mes:</span>
        <select value={mes} onChange={e => setMes(e.target.value)} style={{ width: 'auto' }}>
          {mesesDisponibles.map(c => <option key={c} value={c}>{nombreMes(c)}</option>)}
        </select>
        {presupuestos.some(p => !p.activo) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={verArchivados} onChange={e => setVerArchivados(e.target.checked)} />
            Ver archivados
          </label>
        )}
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="card panel" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: '1rem', marginTop: 0 }}>{editandoId ? 'Editar presupuesto' : 'Nuevo presupuesto'}</h3>
          <form onSubmit={handleGuardar}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label>¿Para qué es?</label>
                <input type="text" value={form.nombre} required autoFocus
                  onChange={e => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Comida casa, Antojos, Transporte" />
              </div>
              <div className="form-group">
                <label>¿Cuánto al mes?</label>
                <input type="number" value={form.monto_mensual} min="0" required
                  onChange={e => setForm({ ...form, monto_mensual: e.target.value })}
                  placeholder="Ej: 100000" />
              </div>
            </div>
            <div className="form-group">
              <label>Icono</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {ICONOS_PRESUPUESTO.map(ic => (
                  <button key={ic} type="button" onClick={() => setForm({ ...form, icono: ic })}
                    style={{
                      width: 38, height: 38, fontSize: '1.3rem', borderRadius: 8, cursor: 'pointer',
                      border: form.icono === ic ? '2px solid var(--primary)' : '2px solid var(--border)',
                      background: form.icono === ic ? 'var(--primary-xlight)' : 'var(--bg-card)',
                    }}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setEditandoId(null); }}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : editandoId ? 'Guardar cambios' : 'Crear presupuesto'}
              </button>
            </div>
          </form>
        </div>
      )}

      {presupuestos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 12 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🧺</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Todavía no tienes presupuestos</p>
          <p style={{ fontSize: '0.9rem' }}>
            Son los gastos que no son fijos pero igual se repiten: la comida de la casa, los antojos,
            el transporte. Defines cuánto quieres gastar al mes y, al registrar cada gasto, ves cuánto
            llevas y cuánto te queda.
          </p>
          <button className="btn-primary" style={{ marginTop: 16 }}
            onClick={() => { setForm(formVacio); setShowForm(true); }}>
            Crear mi primer presupuesto
          </button>
        </div>
      ) : (
        <>
          {/* Totales del mes */}
          <div className="summary-cards" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-icon">🎯</div>
              <div className="card-content">
                <h4>Presupuestado</h4>
                <p className="card-amount neutral">{formatCLP(totales.tope)}</p>
              </div>
            </div>
            <div className="card">
              <div className="card-icon">{totales.restante >= 0 ? '✅' : '⚠️'}</div>
              <div className="card-content">
                <h4>Llevas gastado</h4>
                <p className={`card-amount ${totales.restante >= 0 ? 'neutral' : 'negative'}`}>{formatCLP(totales.usado)}</p>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{Math.round(totales.pct)}% del total</span>
              </div>
            </div>
            <div className="card">
              <div className="card-icon">💰</div>
              <div className="card-content">
                <h4>{totales.restante >= 0 ? 'Te queda' : 'Te pasaste por'}</h4>
                <p className={`card-amount ${totales.restante >= 0 ? 'positive' : 'negative'}`}>{formatCLP(Math.abs(totales.restante))}</p>
              </div>
            </div>
          </div>

          {/* Cada presupuesto */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibles.map(p => {
              const est = estadoPresupuesto(p, gastado[p.id]);
              const abierto = expandido === p.id;
              const detalle = abierto ? gastosDelPresupuesto(movimientos, p.id, mes) : [];
              return (
                <div key={p.id} className="card panel" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', cursor: 'pointer' }}
                    onClick={() => setExpandido(abierto ? null : p.id)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '1.6rem' }}>{p.icono || '🧺'}</span>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <p style={{ margin: 0, fontWeight: 700 }}>{p.nombre}</p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          Llevas <strong>{formatCLP(est.usado)}</strong> de {formatCLP(est.tope)}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem', color: est.color }}>
                          {est.restante >= 0 ? formatCLP(est.restante) : `−${formatCLP(Math.abs(est.restante))}`}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {est.restante >= 0 ? 'te queda' : 'te pasaste'}
                        </p>
                      </div>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{abierto ? '▲' : '▼'}</span>
                    </div>
                    <Barra pct={est.pct} color={est.color} />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{Math.round(est.pct)}% usado</span>
                  </div>

                  {abierto && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '12px 18px', background: 'var(--bg)' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        <button className="btn-secondary" style={{ fontSize: '0.82rem', padding: '6px 12px' }}
                          onClick={() => handleEditar(p)}>✎ Editar</button>
                        <button className="btn-secondary" style={{ fontSize: '0.82rem', padding: '6px 12px' }}
                          onClick={() => handleArchivar(p)} disabled={saving}>
                          {p.activo ? 'Archivar' : 'Reactivar'}
                        </button>
                        <button className="btn-secondary" style={{ fontSize: '0.82rem', padding: '6px 12px', color: 'var(--danger)' }}
                          onClick={() => handleEliminar(p)} disabled={saving}>× Eliminar</button>
                      </div>
                      {detalle.length === 0 ? (
                        <p className="empty-text">Sin gastos de este presupuesto en {nombreMes(mes)}.</p>
                      ) : (
                        <>
                          <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                            {detalle.length} gasto{detalle.length !== 1 ? 's' : ''} en {nombreMes(mes)}
                          </p>
                          {detalle.map(m => (
                            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                              <span>
                                {m.descripcion || 'Gasto'}
                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatFecha(m.fecha)}</span>
                              </span>
                              <strong className="text-danger">−{formatCLP(m.monto)}</strong>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {sinClasificar > 0 && (
            <p className="field-hint" style={{ marginTop: 14 }}>
              Hay {formatCLP(sinClasificar)} en salidas de {nombreMes(mes)} sin presupuesto asignado.
              No todo tiene que tener uno: los gastos fijos, las deudas y las compras del negocio van por su lado.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default Presupuestos;
